import { useState, type ReactNode } from 'react'
import { Plus, Server, Sparkles } from 'lucide-react'
import { cn } from '../lib/cn'
import { Badge } from '../primitives/Badge'
import { Button } from '../primitives/Button'
import { Hint, Label, TextArea } from '../primitives/Field'
import { Toggle } from '../primitives/Toggle'
import { buildInfo, formatBuildDate } from '../shell/BuildInfo'
import { WhatsNewDialog } from '../shell/WhatsNewDialog'
import { SetupView, type LiteLlmDraft } from './SetupView'

interface SystemPrompt {
  id: string
  name: string
  source: 'local' | 'server'
  active?: boolean
}

const SYSTEM_PROMPTS: SystemPrompt[] = [
  { id: 's1', name: 'Sachlich · Standard (DE)', source: 'local', active: true },
  { id: 's2', name: 'Vertrieb · Angebote', source: 'server' },
  { id: 's3', name: 'Recht · Konservativ', source: 'server' },
]

export interface SettingsViewProps {
  /** The stored LiteLLM connection, or null while none is set up. */
  connection: LiteLlmDraft | null
  defaultBaseUrl: string
  onLoadModels: (baseUrl: string, virtualKey: string) => Promise<string[]>
  onSaveConnection: (draft: LiteLlmDraft) => void
  /** Drops the stored key — the app falls back to the setup gate. */
  onDisconnect: () => void
}

export function SettingsView({
  connection,
  defaultBaseUrl,
  onLoadModels,
  onSaveConnection,
  onDisconnect,
}: SettingsViewProps) {
  const [editing, setEditing] = useState(!connection)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [autoSync, setAutoSync] = useState(true)
  const [fromServer, setFromServer] = useState(true)
  const [behavior, setBehavior] = useState('')
  const [activePrompt, setActivePrompt] = useState('s1')
  // „Was ist neu" ist hier jederzeit erreichbar, nicht nur beim ersten Start.
  const [showNotes, setShowNotes] = useState(false)

  if (editing) {
    return (
      <SetupView
        variant="edit"
        initial={connection ?? undefined}
        defaultBaseUrl={defaultBaseUrl}
        onLoadModels={onLoadModels}
        onSave={(draft) => {
          onSaveConnection(draft)
          setEditing(false)
        }}
        onCancel={connection ? () => setEditing(false) : undefined}
      />
    )
  }

  return (
    <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4">
      <Section title="LiteLLM-Verbindung">
        <div className="rounded-xl border border-neutral-200 px-3 py-3">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-brand-600" />
            <span className="truncate text-sm font-medium text-neutral-800">
              {connection?.baseUrl}
            </span>
          </div>
          <dl className="mt-2 space-y-1 text-xs text-neutral-600">
            <div className="flex justify-between gap-3">
              <dt className="text-neutral-400">Chat-Modell</dt>
              <dd className="truncate font-medium text-neutral-800">{connection?.model}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-neutral-400">Spracheingabe</dt>
              <dd className="truncate font-medium text-neutral-800">
                {connection?.transcribeModel || 'aus'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-neutral-400">Virtual Key</dt>
              <dd className="font-medium text-neutral-800">{maskKey(connection?.virtualKey)}</dd>
            </div>
          </dl>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Button size="sm" onClick={() => setEditing(true)}>
            Verbindung ändern
          </Button>
          {confirmDisconnect ? (
            <>
              <Button size="sm" variant="primary" onClick={onDisconnect}>
                Wirklich trennen
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDisconnect(false)}>
                Abbrechen
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmDisconnect(true)}>
              Trennen
            </Button>
          )}
        </div>
        <Hint>Trennen löscht den Virtual Key von diesem Gerät.</Hint>
      </Section>

      <Section title="System-Prompts">
        <p className="-mt-1 mb-2 text-xs text-neutral-400">
          Mehrere System-Prompts verwalten — lokal oder vom Server bezogen.
        </p>
        <div className="space-y-2">
          {SYSTEM_PROMPTS.map((sp) => (
            <button
              key={sp.id}
              type="button"
              onClick={() => setActivePrompt(sp.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left',
                activePrompt === sp.id ? 'border-brand-300 bg-brand-50/40' : 'border-neutral-200',
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-full border',
                  activePrompt === sp.id ? 'border-brand bg-brand' : 'border-neutral-300',
                )}
              >
                {activePrompt === sp.id && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </span>
              <span className="flex-1">
                <span className="block text-sm font-medium text-neutral-800">{sp.name}</span>
                {activePrompt === sp.id && <span className="text-xs text-neutral-400">aktiv</span>}
              </span>
              <Badge tone={sp.source === 'server' ? 'brand' : 'neutral'}>
                {sp.source === 'server' ? 'Server' : 'Lokal'}
              </Badge>
            </button>
          ))}
          <button
            type="button"
            className="flex w-full items-center gap-1.5 px-1 py-1 text-sm font-medium text-brand-600"
          >
            <Plus className="h-4 w-4" /> System-Prompt hinzufügen
          </button>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm font-medium text-neutral-800">Vom Server beziehen</div>
          <Toggle checked={fromServer} onChange={setFromServer} label="Vom Server beziehen" />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-neutral-800">Automatisch synchronisieren</div>
            <div className="text-xs text-neutral-400">Zuletzt synchronisiert: vor 2 Minuten</div>
          </div>
          <Toggle checked={autoSync} onChange={setAutoSync} label="Automatisch synchronisieren" />
        </div>
      </Section>

      <Section title="Verhalten">
        <Label>Anweisungen</Label>
        <TextArea
          value={behavior}
          onChange={(e) => setBehavior(e.target.value)}
          rows={3}
          placeholder="z. B. Nutze einen formellen Ton. Halte dich an die Hauschreibweise."
        />
        <Hint>Gilt für alle Antworten in diesem Add-in.</Hint>
      </Section>

      <Section title="Version">
        <div className="rounded-xl border border-neutral-200 px-3 py-3" data-testid="version-info">
          <dl className="space-y-1 text-xs text-neutral-600">
            <div className="flex justify-between gap-3">
              <dt className="text-neutral-400">Text Assistent</dt>
              <dd className="font-medium text-neutral-800">v{buildInfo.version}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-neutral-400">Build</dt>
              <dd className="font-medium text-neutral-800">
                {formatBuildDate(buildInfo.buildDate)}
              </dd>
            </div>
          </dl>
          <Button size="sm" className="mt-3 w-full" onClick={() => setShowNotes(true)}>
            <Sparkles className="h-4 w-4 text-brand-600" /> Was ist neu
          </Button>
        </div>
        <Hint>Diese Angaben helfen beim Support — bitte bei Rückfragen mitschicken.</Hint>
      </Section>

      {showNotes && <WhatsNewDialog onClose={() => setShowNotes(false)} />}
    </div>
  )
}

/** Show only the tail of the key — enough to tell two keys apart. */
function maskKey(key: string | undefined): string {
  if (!key) return '—'
  return key.length <= 4 ? '••••' : `••••${key.slice(-4)}`
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
        {title}
      </div>
      {children}
    </div>
  )
}
